import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { PinnedVariantTab, PinnedVariantWithStock } from '@/types/sale-type';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  horizontalListSortingStrategy,
  rectSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getStockSnapshot } from './product-sale-utils';

const PIN_TAB_ROW_HEIGHT = 28;
const PIN_TAB_ROW_GAP = 6;
const PIN_TAB_UNPIN_INSET = 6;
const PIN_WRAP_DEFAULT_ROWS = 3;
const PIN_WRAP_COLLAPSED_MAX_HEIGHT =
  PIN_TAB_UNPIN_INSET +
  PIN_WRAP_DEFAULT_ROWS * PIN_TAB_ROW_HEIGHT +
  (PIN_WRAP_DEFAULT_ROWS - 1) * PIN_TAB_ROW_GAP;

const pinTabUnpinClass =
  'absolute right-0 top-0 z-[1] flex h-4 w-4 items-center justify-center rounded-full border bg-white shadow-sm touch-manipulation text-slate-400 hover:border-red-200 hover:bg-red-500 hover:text-white';

const pinTabTriggerClass =
  'h-7 rounded-full pl-2 pr-4 text-[11px] font-medium flex items-center gap-1.5 border transition-all shrink-0 bg-white border-slate-200 text-slate-600 hover:border-blue-300 data-[state=active]:bg-blue-600 data-[state=active]:border-blue-600 data-[state=active]:text-white shadow-sm';

export type ProductSaleTabsV2Props = {
  activeTab: string;
  onTabChange: (tabId: string) => void;
  onRemovePin: (tabId: string, productId: string) => void;
  onReorderPins?: (newOrder: PinnedVariantTab[]) => void;
  pinnedVariants: PinnedVariantWithStock[];
  pinWrapMode?: boolean;
};

type PinTabItemProps = {
  item: PinnedVariantWithStock;
  isActive: boolean;
  onRemovePin: (id: string, productId: string) => void;
};

function PinTabLabel({
  item,
  isActive,
}: {
  item: PinnedVariantWithStock;
  isActive: boolean;
}) {
  const { variant, product } = item;
  const { remain, sold } = getStockSnapshot(variant, product);
  const label = variant.sku || product.name;

  return (
    <>
      <span className="truncate max-w-[80px] pointer-events-none text-[11px] sm:max-w-[120px]">
        {label}
      </span>
      <span
        className={cn(
          'inline-flex items-center justify-center rounded-full text-[9px] font-bold px-1 py-0.5 min-w-[18px] pointer-events-none',
          remain === 0
            ? 'bg-red-700 text-white'
            : sold > 0
              ? 'bg-emerald-100 text-emerald-700'
              : 'bg-slate-100 text-slate-500',
          isActive && 'bg-white/20 text-white',
        )}
      >
        {sold > 0 ? `${sold}/${remain}` : remain}
      </span>
    </>
  );
}

function PinTabUnpinButton({
  isActive,
  onClick,
}: {
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label="Unpin"
      className={cn(pinTabUnpinClass, isActive && 'border-blue-200')}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <X className="h-2.5 w-2.5 stroke-[3px]" />
    </button>
  );
}

function PlainPinTabItem({ item, isActive, onRemovePin }: PinTabItemProps) {
  const { id, product } = item;

  return (
    <div className="relative shrink-0 pt-1 pr-1">
      <TabsTrigger value={id} className={pinTabTriggerClass}>
        <PinTabLabel item={item} isActive={isActive} />
      </TabsTrigger>
      <PinTabUnpinButton
        isActive={isActive}
        onClick={() => onRemovePin(id, product.id)}
      />
    </div>
  );
}

function SortablePinTabItem({ item, isActive, onRemovePin }: PinTabItemProps) {
  const { id, product } = item;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
        zIndex: isDragging ? 10 : undefined,
      }}
      className={cn('relative shrink-0 pt-1 pr-1', isDragging && 'opacity-50')}
    >
      <TabsTrigger
        value={id}
        {...attributes}
        {...listeners}
        className={cn(
          pinTabTriggerClass,
          'touch-none select-none',
          isDragging && 'scale-95 shadow-lg border-blue-500 cursor-grabbing',
        )}
      >
        <PinTabLabel item={item} isActive={isActive} />
      </TabsTrigger>
      <PinTabUnpinButton
        isActive={isActive}
        onClick={() => onRemovePin(id, product.id)}
      />
    </div>
  );
}

export function ProductSaleTabsV2({
  activeTab,
  onTabChange,
  onRemovePin,
  onReorderPins,
  pinnedVariants,
  pinWrapMode = false,
}: ProductSaleTabsV2Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const tabsListRef = useRef<HTMLDivElement>(null);
  const [showArrows, setShowArrows] = useState({ left: false, right: false });
  const [wrapExpanded, setWrapExpanded] = useState(false);
  const [hasWrapOverflow, setHasWrapOverflow] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const sortedVariants = useMemo(() => {
    if (onReorderPins) return pinnedVariants;
    return [...pinnedVariants].sort((a, b) => {
      const soldA = getStockSnapshot(a.variant, a.product).sold;
      const soldB = getStockSnapshot(b.variant, b.product).sold;
      return soldB - soldA;
    });
  }, [pinnedVariants, onReorderPins]);

  const checkScroll = () => {
    if (pinWrapMode) return;
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowArrows({
        left: scrollLeft > 5,
        right: scrollLeft < scrollWidth - clientWidth - 5,
      });
    }
  };

  const checkWrapOverflow = () => {
    if (!pinWrapMode || wrapExpanded) {
      setHasWrapOverflow(false);
      return;
    }
    const el = tabsListRef.current;
    if (!el) return;
    setHasWrapOverflow(el.scrollHeight > PIN_WRAP_COLLAPSED_MAX_HEIGHT + 1);
  };

  useEffect(() => {
    setWrapExpanded(false);
  }, [pinWrapMode]);

  useEffect(() => {
    if (sortedVariants.length === 0) {
      setWrapExpanded(false);
      setHasWrapOverflow(false);
    }
  }, [sortedVariants.length]);

  useEffect(() => {
    checkScroll();
    if (!pinWrapMode) {
      window.addEventListener('resize', checkScroll);
      return () => window.removeEventListener('resize', checkScroll);
    }
  }, [sortedVariants, pinWrapMode]);

  useEffect(() => {
    checkWrapOverflow();
    if (!pinWrapMode) return;
    const el = tabsListRef.current;
    if (!el) return;
    const observer = new ResizeObserver(checkWrapOverflow);
    observer.observe(el);
    window.addEventListener('resize', checkWrapOverflow);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', checkWrapOverflow);
    };
  }, [sortedVariants, pinWrapMode, wrapExpanded]);

  useEffect(() => {
    if (pinWrapMode || !activeTab || !scrollRef.current) return;
    const activeEl = scrollRef.current.querySelector(`[data-state="active"]`);
    activeEl?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [activeTab, pinWrapMode]);

  const handleScroll = (dir: 'l' | 'r') => {
    scrollRef.current?.scrollBy({ left: dir === 'l' ? -200 : 200, behavior: 'smooth' });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (!onReorderPins) return;
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = sortedVariants.findIndex((v) => v.id === active.id);
      const newIndex = sortedVariants.findIndex((v) => v.id === over.id);
      onReorderPins(arrayMove(sortedVariants, oldIndex, newIndex));
    }
  };

  const tabsList = (
    <TabsList
      ref={tabsListRef}
      className={cn(
        'h-auto w-full justify-start bg-transparent p-0 gap-1.5',
        pinWrapMode ? 'flex-wrap pt-1' : 'flex-nowrap',
      )}
      style={
        pinWrapMode && !wrapExpanded && sortedVariants.length > 0
          ? { maxHeight: PIN_WRAP_COLLAPSED_MAX_HEIGHT, overflow: 'hidden' }
          : undefined
      }
    >
      <TabsTrigger
        value="all"
        className="h-7 px-3 rounded-full text-[10px] font-black uppercase border bg-white data-[state=active]:bg-slate-900 data-[state=active]:text-white shrink-0"
      >
        All
      </TabsTrigger>

      {sortedVariants.map((item) => {
        const TabItem = onReorderPins ? SortablePinTabItem : PlainPinTabItem;
        return (
          <TabItem
            key={item.id}
            item={item}
            isActive={activeTab === item.id}
            onRemovePin={onRemovePin}
          />
        );
      })}
    </TabsList>
  );

  const tabsContent = (
    <Tabs value={activeTab} onValueChange={onTabChange} className="w-full">
      {onReorderPins && sortedVariants.length > 0 ? (
        <SortableContext
          items={sortedVariants.map((v) => v.id)}
          strategy={pinWrapMode ? rectSortingStrategy : horizontalListSortingStrategy}
        >
          {tabsList}
        </SortableContext>
      ) : (
        tabsList
      )}
    </Tabs>
  );

  return (
    <div
      className={cn(
        'relative flex bg-slate-50 border-b border-slate-200 shrink-0',
        pinWrapMode ? 'flex-col min-h-14' : 'items-center min-h-14',
      )}
    >
      {!pinWrapMode && showArrows.left && (
        <div className="absolute left-0 z-20 h-full flex items-center bg-gradient-to-r from-slate-100 via-slate-100 to-transparent pl-1 pr-8">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 rounded-full bg-white shadow-md border"
            onClick={() => handleScroll('l')}
          >
            <ChevronLeft className="h-4 w-4 text-blue-600" />
          </Button>
        </div>
      )}

      <div
        ref={scrollRef}
        onScroll={pinWrapMode ? undefined : checkScroll}
        className={cn(
          'flex items-center px-2 pt-1.5 pb-2',
          pinWrapMode ? 'w-full overflow-visible' : 'flex-1 overflow-x-auto overflow-y-visible scrollbar-hide',
        )}
      >
        {onReorderPins && sortedVariants.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            {tabsContent}
          </DndContext>
        ) : (
          tabsContent
        )}
      </div>

      {pinWrapMode && sortedVariants.length > 0 && (hasWrapOverflow || wrapExpanded) && (
        <div className="flex justify-center border-t border-slate-200/80 bg-slate-50 px-2 py-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 gap-1 px-2 text-[10px] font-semibold text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            onClick={() => setWrapExpanded((prev) => !prev)}
          >
            {wrapExpanded ? (
              <>
                <ChevronUp className="h-3 w-3" />
                See less
              </>
            ) : (
              <>
                <ChevronDown className="h-3 w-3" />
                See more
              </>
            )}
          </Button>
        </div>
      )}

      {!pinWrapMode && showArrows.right && (
        <div className="absolute right-0 z-20 h-full flex items-center bg-gradient-to-l from-slate-100 via-slate-100 to-transparent pl-8 pr-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 rounded-full bg-white shadow-md border"
            onClick={() => handleScroll('r')}
          >
            <ChevronRight className="h-4 w-4 text-blue-600" />
          </Button>
        </div>
      )}
    </div>
  );
}
